import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Settings, Clock, RefreshCw } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { houseKioskRoutes } from '../../routes';
import GlassTube from './GlassTube';
import { UserContext } from '../../../contexts/UserContext';
import { useContext, useState, useEffect } from 'react';

export default function DisplayView() {
  const { 
    getHouse, 
    activeHouses, 
    lastUpdated, 
    autoRefreshEnabled, 
    autoRefreshInterval, 
    timeUntilNextRefresh,
    setAutoRefreshEnabled,
    setAutoRefreshInterval,
    forceRefresh 
  } = useApp();
  const { currentUser } = useAuth();
  
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useContext(UserContext);

  const [updatedText, setUpdatedText] = useState('Just now');
  const [isLive, setIsLive] = useState(true);
  const [showRefreshSettings, setShowRefreshSettings] = useState(false);
  const [initialLoadAt] = useState(() => new Date());

  // Format the "Updated" text and track LIVE status
  useEffect(() => {
    const updateDisplay = () => {
      const now = new Date();
      const baseline = lastUpdated ?? initialLoadAt;
      const diffMs = now.getTime() - baseline.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffSecs < 60) {
        setUpdatedText('Just now');
      } else if (diffMins < 60) {
        setUpdatedText(`${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`);
      } else if (diffHours < 24) {
        setUpdatedText(`${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`);
      } else {
        setUpdatedText(`${diffDays} day${diffDays !== 1 ? 's' : ''} ago`);
      }

      // Show LIVE if updated within the last interval period
      const isCurrentlyLive = autoRefreshEnabled && diffMs < autoRefreshInterval + 5000;
      setIsLive(isCurrentlyLive);
    };

    updateDisplay();
    const interval = setInterval(updateDisplay, 1000);
    return () => clearInterval(interval);
  }, [lastUpdated, autoRefreshEnabled, autoRefreshInterval, initialLoadAt]);

  // Sort houses by points for ranking
  const rankings = [...activeHouses].filter(w => w.deactivatedOn == null).sort((a, b) => b.points - a.points);
  const maxPoints = Math.max(...rankings.map(h => h.points), 1);

  const resolvedHouseId = userProfile?.orgs || activeHouses[0]?.id || '';
  const house = getHouse(resolvedHouseId);

  // Determine where to go back to based on user type or referrer
  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);  
    } else if (userProfile?.mainRole === 'TEACHER') {
      navigate(houseKioskRoutes.teacher);
    } else if (userProfile?.mainRole === 'MASCOT') {
      navigate(houseKioskRoutes.mascot(currentUser!.houseId));
    } else if (userProfile?.mainRole === 'KIOSK') {
      navigate(houseKioskRoutes.kiosk);
    } else {
      navigate(houseKioskRoutes.login);
    }
  };

  // Dynamic grid layout based on house count - use full width to fit all houses
  const getGridClass = () => {
    const count = rankings.length;
    if (count <= 1) return 'grid-cols-1 max-w-md';
    if (count === 2) return 'grid-cols-2 max-w-3xl';
    if (count === 3) return 'grid-cols-3 max-w-5xl';
    if (count === 4) return 'grid-cols-2 md:grid-cols-4 max-w-full px-4';
    if (count === 5) return 'grid-cols-3 md:grid-cols-5 max-w-full px-4';
    if (count === 6) return 'grid-cols-3 md:grid-cols-6 max-w-full px-4';
    // For 7+ houses, use responsive grid that fits all horizontally
    return `grid-cols-${Math.min(count, 4)} md:grid-cols-${Math.min(count, 6)} lg:grid-cols-${count} max-w-full px-2`;
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div
        className="absolute inset-0 opacity-20 animate-pulse"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 215, 0, 0.2) 0%, transparent 70%)',
          animation: 'flicker 4s ease-in-out infinite',
        }}
      />

      {/* Auto-refresh Settings Modal */}
      {showRefreshSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-[400px]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Auto-refresh Settings</h3>
              <button
                onClick={() => setShowRefreshSettings(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Enable Auto-refresh</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRefreshEnabled}
                    onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Refresh Interval */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Refresh Interval</label>
                <select
                  value={autoRefreshInterval}
                  onChange={(e) => setAutoRefreshInterval(parseInt(e.target.value, 10))}
                  disabled={!autoRefreshEnabled}
                  className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value={10000}>10 seconds</option>
                  <option value={30000}>30 seconds</option>
                  <option value={60000}>1 minute</option>
                  <option value={300000}>5 minutes</option>
                  <option value={600000}>10 minutes</option>
                </select>
              </div>

              {/* Status Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Status</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Last Updated:</span>
                    <span className="font-medium">{updatedText}</span>
                  </div>
                  {autoRefreshEnabled && timeUntilNextRefresh > 0 && (
                    <div className="flex justify-between">
                      <span>Next Refresh:</span>
                      <span className="font-medium">{Math.ceil(timeUntilNextRefresh / 1000)}s</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  Auto-refresh pauses when the browser tab is not active to save resources.
                  Real-time updates via SignalR will continue to work regardless of this setting.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowRefreshSettings(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Close
              </button>
              <button
                onClick={async () => {
                  await forceRefresh();
                }}
                className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Now
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 min-h-screen flex flex-col p-8 py-12">
        {/* Refresh Controls - Top Right */}
        <div className="absolute top-8 right-8 flex items-center gap-3">
          <button
            onClick={async () => await forceRefresh()}
            className="backdrop-blur-lg bg-white/10 hover:bg-white/20 text-white font-bold py-2 px-4 rounded-full transition-all duration-300 border-2 border-white/30 hover:border-white/50 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center space-x-2"
            title="Refresh Now"
          >
            <RefreshCw size={18} />
            <span className="text-sm">Refresh</span>
          </button>
          <button
            onClick={() => setShowRefreshSettings(true)}
            className="backdrop-blur-lg bg-white/10 hover:bg-white/20 text-white font-bold p-2 rounded-full transition-all duration-300 border-2 border-white/30 hover:border-white/50 shadow-xl hover:shadow-2xl hover:scale-105"
            title="Auto-refresh Settings"
          >
            <Settings size={20} />
          </button>
        </div>

        {(house || userProfile?.mainRole == 'KIOSK') && (
        <button
          onClick={handleBack}
          className="self-start mb-8 backdrop-blur-lg bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 border-2 border-white/30 hover:border-white/50 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center space-x-2"
        >
          <ArrowLeft size={20} />
          <span>BACK</span>
        </button>
        )}

        <div className="flex-1 flex flex-col items-center justify-center space-y-12">
          <div className="text-center space-y-4">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 tracking-wider">
              HOUSE POINTS
            </h1>
            <div className="h-1 w-48 mx-auto bg-gradient-to-r from-transparent via-yellow-500 to-transparent rounded-full" />
          </div>

          {rankings.length === 0 ? (
            <div className="text-white/60 text-xl">No active houses</div>
          ) : (
            <div className={`grid ${getGridClass()} gap-4 md:gap-6 lg:gap-8 w-full`}>
              {rankings.map((house, index) => (
                <GlassTube
                  key={house.id}
                  house={house}
                  rank={index + 1}
                  maxPoints={maxPoints}
                />
              ))}
            </div>
          )}

          <div className="text-center space-y-2">
            <div className="flex items-center justify-center space-x-2">
              <Clock className="w-4 h-4 text-white/60" />
              <p className="text-white/60 text-sm">Updated: {updatedText}</p>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
              <p className={`text-xs font-semibold ${isLive ? 'text-green-400' : 'text-gray-400'}`}>
                {isLive ? 'LIVE' : autoRefreshEnabled ? 'LOADING' : 'OFFLINE'}
              </p>
            </div>
            {autoRefreshEnabled && timeUntilNextRefresh > 0 && (
              <p className="text-white/40 text-xs">
                Next update in {Math.ceil(timeUntilNextRefresh / 1000)}s
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
